import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (data) => api.post('/auth/change-password', data),
  unlockEarlyAccess: (accessKey) => api.post('/auth/early-access', { accessKey })
};

// Character endpoints
export const characterAPI = {
  getAll: () => api.get('/characters'),
  getOne: (id) => api.get(`/characters/${id}`),
  getCreationData: () => api.get('/characters/creation-data'),
  create: (data) => api.post('/characters', data),
  update: (id, data) => api.patch(`/characters/${id}`, data),
  delete: (id) => api.delete(`/characters/${id}`),
  generateBackstory: (data) => api.post('/characters/generate-backstory', data),
  generateAppearance: (data) => api.post('/characters/generate-appearance', data),
  generatePortrait: (data) => api.post('/characters/generate-portrait', data)
};

// Game endpoints
export const gameAPI = {
  getAll: () => api.get('/games'),
  getOne: (id) => api.get(`/games/${id}`),
  create: (data) => api.post('/games/new', data),
  sendAction: (id, action, rollResult = null) =>
    api.post(`/games/${id}/action`, { action, rollResult }),
  submitRoll: (id, rollData) => api.post(`/games/${id}/roll`, rollData),
  getJournal: (id) => api.get(`/games/${id}/journal`),
  addNote: (id, title, content) => api.post(`/games/${id}/journal/note`, { title, content }),
  consumeItem: (id, itemName) => api.post(`/games/${id}/consume`, { itemName }),
  dropItem: (id, itemId) => api.post(`/games/${id}/drop-item`, { itemId }),
  endCombat: (id) => api.post(`/games/${id}/end-combat`),
  sleep: (id, hours) => api.post(`/games/${id}/sleep`, { hours }),
  wait: (id, hours) => api.post(`/games/${id}/wait`, { hours }),
  updateSettings: (id, settings) => api.patch(`/games/${id}/settings`, settings),
  delete: (id) => api.delete(`/games/${id}`)
};

// Admin endpoints
export const adminAPI = {
  getStatus: () => api.get('/admin/status'),
  sendCommand: (input, gameId) => api.post('/admin/command', { input, gameId })
};

// Session/Multiplayer endpoints
export const sessionAPI = {
  // Invite codes
  generateInvite: (gameId) => api.post(`/session/${gameId}/invite`),
  generateInviteCode: (gameId) => api.post(`/session/${gameId}/invite`),
  revokeInvite: (gameId) => api.delete(`/session/${gameId}/invite`),
  revokeInviteCode: (gameId) => api.delete(`/session/${gameId}/invite`),
  getInviteStatus: (gameId) => api.get(`/session/${gameId}/invite`),
  toggleOpenSession: (gameId, open) => api.post(`/session/${gameId}/open-session`, { open }),
  toggleSessionOpen: (gameId) => api.patch(`/session/${gameId}/toggle-open`),

  // Joining/Leaving
  joinGame: (inviteCode, characterId) => api.post('/session/join', { inviteCode, characterId }),
  joinSession: (inviteCode, characterId) => api.post('/session/join', { inviteCode, characterId }),
  leaveGame: (gameId) => api.post(`/session/${gameId}/leave`),
  kickPlayer: (gameId, userId) => api.post(`/session/${gameId}/kick/${userId}`),

  // Participants
  getParticipants: (gameId) => api.get(`/session/${gameId}/participants`),

  // Turn management
  startTurns: (gameId) => api.post(`/session/${gameId}/turns/start`),
  endTurn: (gameId) => api.post(`/session/${gameId}/turns/end`),
  getTurnState: (gameId) => api.get(`/session/${gameId}/turns`),
  checkTurn: (gameId) => api.post(`/session/${gameId}/turns/check`),
  forceHostTurn: (gameId) => api.post(`/session/${gameId}/turns/force-host`),

  // Ban management
  banPlayer: (gameId, userId) => api.post(`/session/${gameId}/ban/${userId}`),
  unbanPlayer: (gameId, userId) => api.post(`/session/${gameId}/unban/${userId}`),

  // OOC Chat
  sendOOC: (gameId, content) => api.post(`/session/${gameId}/ooc`, { content })
};

// Profile endpoints
export const profileAPI = {
  // Profile
  getMe: () => api.get('/profile/me'),
  updateMe: (data) => api.patch('/profile/me', data),
  getUser: (userId) => api.get(`/profile/user/${userId}`),

  // Friends
  sendFriendRequest: (userId) => api.post(`/profile/friends/request/${userId}`),
  getFriendRequests: () => api.get('/profile/friends/requests'),
  respondToFriendRequest: (requestId, action) =>
    api.patch(`/profile/friends/request/${requestId}`, { action }),
  removeFriend: (userId) => api.delete(`/profile/friends/${userId}`),

  // Blocking
  blockUser: (userId) => api.post(`/profile/block/${userId}`),
  unblockUser: (userId) => api.delete(`/profile/block/${userId}`),
  getBlockedUsers: () => api.get('/profile/blocked'),

  // Private Messages
  sendMessage: (userId, content) => api.post(`/profile/messages/${userId}`, { content }),
  getConversation: (userId, limit = 50, before = null) =>
    api.get(`/profile/messages/${userId}`, { params: { limit, before } }),
  getConversations: () => api.get('/profile/conversations'),
  getUnreadCount: () => api.get('/profile/messages/unread/count')
};

export default api;
